import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { api, clearSession } from "../lib/api";
import { setTableUnlocks } from "../lib/tableThemes";
import { refreshTableUnlocks } from "../lib/tableUnlocks";
import { forgetGuest, keepGuest, knownGuest } from "../lib/guestDevice";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // If returning from Google OAuth callback, skip the /me check.
    // AuthCallback will exchange the code and establish the session first.
    if (window.location.search?.includes("code=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  // Table colours unlocked with a video belong to the account (lib/tableUnlocks.js).
  const userId = user?.user_id;
  useEffect(() => {
    if (userId) refreshTableUnlocks().catch(() => {});
    else if (!loading) setTableUnlocks({});
  }, [userId, loading]);

  // A guest is kept on its device (lib/guestDevice.js): its key, and the name and picture shown to come back.
  const isGuest = !!user?.is_guest;
  const userName = user?.name;
  const userPicture = user?.picture;
  useEffect(() => {
    if (isGuest) keepGuest({ user_id: userId, name: userName, picture: userPicture, is_guest: true }).catch(() => {});
  }, [isGuest, userId, userName, userPicture]);

  // Signing out keeps the device's guest: « Continuer en tant que … » brings it back.
  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {}
    clearSession();
    setUser(null);
  };

  // For good: the server leaves the player's rooms, then deletes the account and its sessions.
  // Throws when the server refuses, and the player stays signed in.
  const deleteAccount = async () => {
    await api.delete("/account");
    if (user?.is_guest && (await knownGuest())?.user_id === user.user_id) await forgetGuest();
    clearSession();
    setUser(null);
  };

  // The Lobby's Profil tab (components/lobby/ProfileTab.jsx): only the fields sent change (`avatar: null` =
  // the Google photo, or the initial for a guest). Throws when the server refuses; the account it
  // sends back carries the name and picture to show from now on.
  const updateProfile = async (changes) => {
    const { data } = await api.patch("/profile", changes);
    setUser((u) => ({ ...u, ...data.user }));
    return data.user;
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, logout, deleteAccount, updateProfile, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
